import React, { InputHTMLAttributes, useRef, useState } from 'react';
import styled from 'styled-components';
import { lightTheme } from '../../styles/theme';

type InputStatusType = 'default' | 'danger' | 'alert' | 'active';

interface RoundedInputProps extends InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	info?: string;
	status?: InputStatusType;
	hidable?: boolean;
	bordered?: boolean;
}

export const Input: React.FC<RoundedInputProps> = ({
	label,
	info,
	status,
	hidable = false,
	bordered = false,
	...props
}) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [isInFocus, setIsInFocus] = useState(false);
	const [isPassShown, setIsPassShown] = useState(false);

	const putCursorAtTheEnd = () => {
		if (inputRef.current) {
			inputRef.current.focus();
			setTimeout(() => {
				const length = inputRef.current?.value.length || 0;
				inputRef.current?.setSelectionRange(length, length);
			}, 0);
		}
	};

	return (
		<>
			{label && <Label>{label}</Label>}
			<Border
				{...props}
				status={(status && status) || (isInFocus && 'active') || 'default'}
			>
				<InputWrapper
					disabled={props.disabled}
					bordered={bordered}
					onClick={() => {
						if (inputRef.current) {
							inputRef.current.focus();
						}
					}}
				>
					<StyledInput
						{...props}
						type={isPassShown ? 'text' : props.type}
						ref={inputRef}
						onFocus={() => setIsInFocus(true)}
						onBlur={() => setIsInFocus(false)}
					/>
					{!props.disabled && hidable && props.value && (
						<Lock
							onClick={() => {
								putCursorAtTheEnd();
								setIsPassShown(!isPassShown);
							}}
						>
							{isPassShown ? '🔓' : '🔒'}
						</Lock>
					)}
				</InputWrapper>
			</Border>
			{info && <Info status={status || 'default'}>{info}</Info>}
		</>
	);
};

const Border = styled.div<{ status: InputStatusType }>`
	border: 2px solid
		${({ status }) => {
			if (status === 'danger') return lightTheme.inputs.border.danger;
			if (status === 'alert') return lightTheme.inputs.border.alert;
			if (status === 'active') return lightTheme.inputs.border.active;
			if (status === 'default') return 'transparent';
		}};
	border-radius: 10px;
	padding: 1px;
`;

const InputWrapper = styled.div<{ bordered: boolean; disabled?: boolean }>`
	display: flex;
	align-items: center;
	width: 100%;
	height: 40px;
	padding: 8px 12px;
	border-radius: 8px;
	transition: 0.1s;
	box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
	cursor: text;
	border: 1px solid transparent;
	background: ${({ disabled }) =>
		disabled
			? lightTheme.inputs.background.disabled
			: lightTheme.inputs.background.default};
	border-color: ${({ theme, bordered }) =>
		bordered && lightTheme.inputs.border.light};

	&:focus-within {
		border-color: ${lightTheme.inputs.border.default};
	}
`;

const StyledInput = styled.input<{ disabled?: boolean }>`
	flex: 1;
	border: none;
	outline: none;
	background: transparent;
	font-size: 14px;
	color: ${({ disabled }) =>
		disabled ? lightTheme.typography.tetriary : lightTheme.typography.primary};

	&::placeholder {
		color: ${lightTheme.inputs.text.placeholder};
	}
`;

const Label = styled.p`
	font-size: 14px;
	font-weight: 500;
	margin-bottom: 3px;
`;

const Info = styled.p<{ status: InputStatusType }>`
	font-size: 14px;
	font-weight: 400;
	margin-top: 3px;

	color: ${({ status }) => {
		if (status === 'danger') return lightTheme.inputs.text.infoDanger;
		if (status === 'alert') return lightTheme.inputs.text.infoAlert;
		if (status === 'default') return lightTheme.inputs.text.infoDefault;
	}};
`;

const Lock = styled.p`
	cursor: pointer;
`;
